import { duelSpec } from './duelSpecs';

/**
 * How the questions get harder as a run goes on (SPEC-007 PR-7.8).
 *
 * The clock already makes a run harder by giving less time. That is pressure,
 * not difficulty — the eleventh question is the same question as the first,
 * asked in a hurry. What actually separates a reader who knows these cars from
 * one who is guessing quickly is WHICH question they are asked, and how close
 * the two answers are.
 *
 * So a round is described by two numbers, and they move together:
 *
 *   TIER      which subjects may be asked. Round one asks about top speed,
 *             power and cylinders — things a child can have an opinion about
 *             from the pictures. Torque, power-to-weight, the standing quarter
 *             and how many were built arrive later, because a reader who has
 *             never seen the figure cannot be expected to have a feel for it.
 *   GAP       how far apart the two answers must be, as a fraction of the
 *             larger. Early rounds only ask about pairs that are not close;
 *             later ones allow the near-misses the pool already considers fair.
 *
 * The gap is the half that does the quiet work. A first round pairing the F40
 * against the Diablo on top speed is technically answerable and is a coin toss
 * to anybody; the same pairing at round nine is a real question for somebody who
 * has been paying attention. Same pool, same rules, different round.
 *
 * Pure arithmetic over the round number. It never sees a pool or a clock.
 */

/**
 * The curve, as a table rather than as a formula.
 *
 * Written out because every row is a product decision somebody may want to argue
 * with, and an expression like `Math.min(3, Math.ceil(round / 3))` hides all
 * three of them behind one piece of arithmetic. `from` is the first round the
 * row applies to, and the last row has no end.
 */
const CURVE = [
    // The opening. One subject family, and only pairings that are not close —
    // a reader's first question should never be one they could not have got.
    { from: 1, tier: 1, gap: 0.22 },
    // The middle. Size, weight and acceleration join, and the pairings tighten.
    { from: 4, tier: 2, gap: 0.12 },
    // The specialist end, still holding pairs apart enough to be answerable.
    { from: 7, tier: 3, gap: 0.06 },
    // Everything, at whatever margin the pool itself considers fair. Reaching
    // here is a very good run, and it is where the game stops helping.
    { from: 10, tier: 3, gap: 0 },
];

/**
 * Which row of the curve this round is on, from 0.
 *
 * The curve's own name for "how far into a run this is", and it is exported
 * because `duelScoring` prices progression against it (SPEC-007 D-78). A second
 * table of round numbers over there would be a second opinion about where the
 * middle of a run begins, and the two would drift the first time either moved.
 */
export function roundStage(round) {
    return CURVE.indexOf(rowFor(round));
}

/** How many rows the curve has, which is where progression stops climbing. */
export const DIFFICULTY_STAGES = CURVE.length;

/**
 * The first round on the LAST row — where the pool opens up completely and the
 * curve stops moving.
 *
 * Exported because two other modules need to talk about "a round the game has
 * stopped helping with" without knowing that it is called ten.
 */
export const LAST_STAGE_ROUND = CURVE[CURVE.length - 1].from;

/** The hardest tier a question may be drawn from this round. */
export function roundTier(round) {
    return rowFor(round).tier;
}

/** The smallest relative gap a pairing may have this round. */
export function roundGap(round) {
    return rowFor(round).gap;
}

function rowFor(round) {
    const number = Number.isFinite(round) && round >= 1 ? Math.trunc(round) : 1;

    // Walked backwards, so the LAST row whose `from` has been reached wins. A
    // forward `find` would answer the opening row for every round.
    return [...CURVE].reverse().find((row) => number >= row.from) ?? CURVE[0];
}

/**
 * Is this duel one this round may ask?
 *
 * Both halves, in one question, because a caller filtering a pool has no reason
 * to care that the answer is made of two — and because the day a third axis is
 * added this is the one place it lands.
 */
export function isRoundCandidate(duel, round) {
    const spec = duelSpec(duel?.spec);

    if (!spec) {
        return false;
    }

    return spec.difficulty <= roundTier(round) && (duel.gap ?? 0) >= roundGap(round);
}

export { CURVE as DIFFICULTY_CURVE };
